<template>
	<section
		class="w-full faq-accordion"
		aria-label="Frequently Asked Questions"
	>
		<article
			v-for="(item, index) in items"
			:id="`faq-item-${index}`"
			:key="`faq-item-${index}`"
			class="border-x border-t last:border-b first:rounded-t last:rounded-b p-8 flex flex-col scroll-my-36"
		>
			<h3 class="w-full m-0">
				<button
					class="w-full flex justify-between items-center text-left focus:outline-none"
					:aria-expanded="openItems.includes(index)"
					:aria-controls="`faq-content-${index}`"
					itemprop="name"
					@click="toggleItem(index)"
				>
					<span class="text-title-size-lg">{{ item.title }}</span>
					<span
						class="text-brand-600 transition-transform duration-300 size-10"
						:class="{ 'rotate-[225deg]': !openItems.includes(index) }"
						aria-hidden="true"
					>
						<AtomFontAwesome
							icon="fa-xmark"
							class="size-10"
						/>
					</span>
				</button>
			</h3>
			<div
				:id="`faq-content-${index}`"
				:aria-hidden="!openItems.includes(index)"
				class="overflow-hidden transition-all duration-300"
				:class="{
					'max-h-0 opacity-0': !openItems.includes(index),
					'max-h-96 pb-4 pt-8 opacity-100': openItems.includes(index),
				}"
			>
				<div
					itemprop="text"
					class="text-paragraph-size-md"
				>
					{{ item.description }}
				</div>
			</div>
		</article>
	</section>
</template>

<script setup lang="ts">
import type { FaqItemType } from '#base/core/types/FaqItemType';
import { formatSchema } from '#base/helpers/schemaHelpers';

const props = defineProps<{
	items: FaqItemType[];
	initialOpenIndex?: number;
}>();

const openItems = ref<number[]>([]);

const route = useRoute();

// Initialize with the initial open item if provided
onMounted(() => {
	if (props.initialOpenIndex !== undefined) {
		openItems.value = [props.initialOpenIndex];
	}

	if (route.hash && route.hash.includes('#faq-item-')) {
		openItems.value = [Number(route.hash.replace('#faq-item-', ''))];
	}
});

function toggleItem(index: number) {
	if (openItems.value.includes(index)) {
		// Close the item
		openItems.value = openItems.value.filter(i => i !== index);
	}
	else {
		// Open the item
		openItems.value.push(index);
	}
}

useHead({
	script: [
		formatSchema({
			'@context': 'https://schema.org',
			'@type': 'FAQPage',
			'mainEntity': props.items.map(item => ({
				'@type': 'Question',
				'name': item.title,
				'acceptedAnswer': {
					'@type': 'Answer',
					'text': item.description,
				},
			})),
		}),
	],
});
</script>
